import { apiFetch } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import type {
  EnrichedCheckin,
  SupplierBoxSummary,
  PageResponse,
  ToggleCheckinStatusPayload,
  AddReceiptPayload,
  CloseShortfallPayload,
  RequestReturnPayload,
  DecideReturnPayload,
  CheckinPageQuery,
  CheckinAuditEntry,
  BoxMovementPage,
} from "./types";

export function listCheckins(
  limit = 2000,
  offset = 0,
): Promise<PageResponse<EnrichedCheckin>> {
  const qs = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  return apiFetch(`${ENDPOINTS.checkins.list}?${qs}`);
}

export function listCheckinsPage(
  query: CheckinPageQuery,
  signal?: AbortSignal,
): Promise<PageResponse<EnrichedCheckin>> {
  const params = new URLSearchParams({
    limit: String(query.limit),
    offset: String(query.offset),
  });
  const search = query.search.trim();
  const date = query.date?.trim();

  if (search) params.set("search", search);
  if (query.status) params.set("status", query.status);
  if (date) params.set("date", date);

  return apiFetch(`${ENDPOINTS.checkins.list}?${params}`, { signal });
}

export function getCheckin(id: string): Promise<EnrichedCheckin> {
  return apiFetch(ENDPOINTS.checkins.detail(id));
}

export async function getCheckinAudit(
  id: string,
): Promise<PageResponse<CheckinAuditEntry>> {
  const limit = 200;
  const items: CheckinAuditEntry[] = [];
  let total = 0;

  do {
    const page = await apiFetch<PageResponse<CheckinAuditEntry>>(
      `${ENDPOINTS.checkins.audit(id)}?limit=${limit}&offset=${items.length}`,
    );
    items.push(...page.items);
    total = page.total;
    if (page.items.length === 0) break;
  } while (items.length < total);

  return { items, total, limit: items.length, offset: 0 };
}

export function toggleCheckinStatus(
  id: string,
  payload: ToggleCheckinStatusPayload,
): Promise<EnrichedCheckin> {
  return apiFetch(ENDPOINTS.checkins.toggleStatus(id), {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function createCheckin(orderID: string): Promise<EnrichedCheckin> {
  return apiFetch(ENDPOINTS.checkins.list, {
    method: "POST",
    body: JSON.stringify({ order_id: orderID }),
  });
}

export function deleteCheckin(id: string): Promise<void> {
  return apiFetch(ENDPOINTS.checkins.detail(id), { method: "DELETE" });
}

export function checkItem(
  checkinId: string,
  itemId: string,
  receivedQuantity: number,
): Promise<EnrichedCheckin> {
  return apiFetch(ENDPOINTS.checkins.checkItem(checkinId, itemId), {
    method: "POST",
    body: JSON.stringify({ received_quantity: receivedQuantity }),
  });
}

export function quickCheckItem(
  checkinId: string,
  itemId: string,
): Promise<EnrichedCheckin> {
  return apiFetch(ENDPOINTS.checkins.quickCheckItem(checkinId, itemId), {
    method: "POST",
  });
}

export function undoCheckItem(
  checkinId: string,
  itemId: string,
): Promise<EnrichedCheckin> {
  return apiFetch(ENDPOINTS.checkins.undoCheckItem(checkinId, itemId), {
    method: "POST",
  });
}

export function addReceipt(
  checkinId: string,
  itemId: string,
  payload: AddReceiptPayload,
): Promise<EnrichedCheckin> {
  return apiFetch(ENDPOINTS.checkins.addReceipt(checkinId, itemId), {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function closeShortfall(
  checkinId: string,
  itemId: string,
  payload: CloseShortfallPayload,
): Promise<EnrichedCheckin> {
  return apiFetch(ENDPOINTS.checkins.closeShortfall(checkinId, itemId), {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function undoReceivingAction(
  checkinId: string,
  itemId: string,
): Promise<EnrichedCheckin> {
  return apiFetch(ENDPOINTS.checkins.undoReceivingAction(checkinId, itemId), {
    method: "POST",
  });
}

export function undoReceivingHistory(
  checkinId: string,
  itemId: string,
  auditId: number,
): Promise<EnrichedCheckin> {
  return apiFetch(
    `${ENDPOINTS.checkins.detail(checkinId)}/items/${itemId}/history/${auditId}/undo-receiving`,
    { method: "POST" },
  );
}

export function undoReturnHistory(
  checkinId: string,
  itemId: string,
  auditId: number,
): Promise<EnrichedCheckin> {
  return apiFetch(
    `${ENDPOINTS.checkins.detail(checkinId)}/items/${itemId}/history/${auditId}/undo-return`,
    { method: "POST" },
  );
}

export function undoCheckHistory(
  checkinId: string,
  itemId: string,
  auditId: number,
): Promise<EnrichedCheckin> {
  return apiFetch(
    `${ENDPOINTS.checkins.detail(checkinId)}/items/${itemId}/history/${auditId}/undo-check`,
    { method: "POST" },
  );
}

export function undoLifecycleHistory(
  checkinId: string,
  auditId: number,
): Promise<EnrichedCheckin> {
  return apiFetch(
    `${ENDPOINTS.checkins.detail(checkinId)}/history/${auditId}/undo-lifecycle`,
    { method: "POST" },
  );
}

export function reopenShortfallItem(
  checkinId: string,
  itemId: string,
): Promise<EnrichedCheckin> {
  return apiFetch(ENDPOINTS.checkins.shortfallReopenItem(checkinId, itemId), {
    method: "POST",
  });
}

export function undoReturnAction(
  checkinId: string,
  itemId: string,
): Promise<EnrichedCheckin> {
  return apiFetch(ENDPOINTS.checkins.undoReturnAction(checkinId, itemId), {
    method: "POST",
  });
}

export function requestReturn(
  checkinId: string,
  itemId: string,
  payload: RequestReturnPayload,
): Promise<EnrichedCheckin> {
  return apiFetch(ENDPOINTS.checkins.requestReturn(checkinId, itemId), {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function decideReturn(
  checkinId: string,
  itemId: string,
  payload: DecideReturnPayload,
): Promise<EnrichedCheckin> {
  return apiFetch(ENDPOINTS.checkins.decideReturn(checkinId, itemId), {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function reopenReturnDecision(
  checkinId: string,
  itemId: string,
): Promise<EnrichedCheckin> {
  return apiFetch(ENDPOINTS.checkins.reopenReturnDecision(checkinId, itemId), {
    method: "POST",
  });
}

export function getSupplierBoxSummaries(
  checkinId: string,
): Promise<SupplierBoxSummary[]> {
  return apiFetch(ENDPOINTS.checkins.suppliers(checkinId));
}

export function getBoxMovements(
  checkinId: string,
  supplierId: string,
  offset = 0,
): Promise<BoxMovementPage> {
  const path = `${ENDPOINTS.checkins.detail(encodeURIComponent(checkinId))}/suppliers/${encodeURIComponent(supplierId)}/box-movements`;
  return apiFetch(`${path}?limit=20&offset=${offset}`);
}

export function undoBoxMovement(
  checkinId: string,
  supplierId: string,
  movementId: string,
): Promise<{
  new_balance: number;
  original_transaction_ids: string[];
  undo_transaction_ids: string[];
}> {
  return apiFetch(
    `${ENDPOINTS.checkins.detail(encodeURIComponent(checkinId))}/suppliers/${encodeURIComponent(supplierId)}/box-movements/${encodeURIComponent(movementId)}/undo`,
    { method: "POST" },
  );
}

export function updateBoxTransaction(
  checkinId: string,
  supplierID: string,
  boxesSent: number,
  boxesReceived: number,
): Promise<{ new_balance: number }> {
  return apiFetch(ENDPOINTS.checkins.boxTransaction(checkinId), {
    method: "POST",
    body: JSON.stringify({
      supplier_id: supplierID,
      boxes_sent: boxesSent,
      boxes_received: boxesReceived,
    }),
  });
}
